import {Assertion} from '@udemy/badging';
import {ClientEvent} from '@udemy/event-tracking';

import {BadgeAssertionQuery, OrganizationBadgeStatus} from '../generated/graphql';

export interface BadgeClass {
    name: string;
    id?: string;
    isOrganizationBadge?: boolean;
    organizationBadgeStatus?: TrackedOrganizationBadgeStatus;
}

export type TrackedOrganizationBadgeStatus = 'draft' | 'published' | 'expired';

/**
 * Maps the GraphQL `OrganizationBadgeStatus` enum onto the lower-cased values
 * the badge event schemas declare. Typed as a total map, so a status added to
 * the schema fails to compile until it is handled here.
 */
const ORGANIZATION_BADGE_STATUSES: Record<OrganizationBadgeStatus, TrackedOrganizationBadgeStatus> =
    {
        [OrganizationBadgeStatus.Draft]: 'draft',
        [OrganizationBadgeStatus.Published]: 'published',
        [OrganizationBadgeStatus.Expired]: 'expired',
    };

/**
 * The queried shape of `badgeClass`: a discriminated union whose `__typename`
 * the assertion query always requests, and which carries `status` only on
 * organization badges. Neither field is part of the `Assertion` type, which is
 * what `useAssertion` casts the response to.
 */
type QueriedBadgeClass = NonNullable<BadgeAssertionQuery['badgeAssertion']>['badgeClass'];

function toTrackedBadgeClass(badgeClass: Assertion['badgeClass']): BadgeClass {
    const queried = badgeClass as unknown as QueriedBadgeClass;
    const isOrganizationBadge = queried.__typename === 'OrganizationBadgeClass';

    return {
        name: badgeClass.name,
        id: badgeClass.externalUrl,
        isOrganizationBadge,
        organizationBadgeStatus: isOrganizationBadge
            ? ORGANIZATION_BADGE_STATUSES[queried.status]
            : undefined,
    };
}

/**
 * Destinations a badge assertion can be shared to. The value must match the
 * `destination` options declared in the BadgeAssertionShared schema.
 */
export const SHARE_DESTINATIONS = {
    LINKEDIN_CERTIFICATION: 'linkedin_certification',
} as const;

export type ShareDestination = (typeof SHARE_DESTINATIONS)[keyof typeof SHARE_DESTINATIONS];

/*
 * This is a list of all the events that are sent to the tracker.
 * We might need to move this when we add context menu to frontend components.
 */
export class BadgeAssertionRemoveSelectedEvent extends ClientEvent {
    badgeClass: BadgeClass;

    constructor(badgeClass: BadgeClass) {
        super('BadgeAssertionRemoveSelected');
        this.badgeClass = badgeClass;
    }
}

export class BadgeAssertionRemovedEvent extends ClientEvent {
    badgeClass: BadgeClass;

    constructor(badgeClass: BadgeClass) {
        super('BadgeAssertionRemoved');
        this.badgeClass = badgeClass;
    }
}

export class BadgeAssertionSharedEvent extends ClientEvent {
    badgeClass: BadgeClass;
    destination: ShareDestination;

    constructor(badgeClass: Assertion['badgeClass'], destination: ShareDestination) {
        super('BadgeAssertionShared');
        this.badgeClass = toTrackedBadgeClass(badgeClass);
        this.destination = destination;
    }
}
